import React, {useEffect, useState, useRef} from "react";
import {eliminarArchivo, obtenerArchivos, subirArchivo, archivarArchivo} from "./AccionesBackend";

const GestorArchivos = ({seccionId, archivoId, acceptedFileTypes, onAfterUpload, mostrarFormulario = true, mostrarPreview = true }) => {
    const [files, setFiles] = useState([]);
    const [newFile, setNewFile] = useState(null);
    const [error, setError] = useState("");
    const [successMsg, setSuccessMsg] = useState("");
    const fileInputRef = useRef(null);
    const [archivado, setArchivado] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!seccionId && !archivoId) return;

        obtenerArchivos({seccionId, archivoId, archivado})
            .then((result) => {
                if (result && result.success) {
                    setFiles(result.archivos);
                    setError("");
                } else {
                    setFiles([]);
                    setError("No se pudieron cargar los archivos / No hay archivos.");
                }
            })
            .catch((err) => {
                console.error("Error al obtener los archivos:", err);
                setFiles([]);
                setError("No se pudieron cargar los archivos / No hay archivos.");
            });
    }, [seccionId, archivoId, archivado]);

    const handleAddFile = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!newFile) {
            setError("Selecciona un archivo para subir.");
            return;
        }

        setIsLoading(true);

        try {
            const result = await subirArchivo({
                archivo: newFile,
                usuarioId: 1,
                seccionId,
            });

            if (result.success) {
                setFiles([...files, result.archivo]);
                setNewFile(null);
                setError("");
                setSuccessMsg("✅ Archivo guardado correctamente.");
                setTimeout(() => setSuccessMsg(""), 3000);

                if (typeof onAfterUpload === "function") {
                    onAfterUpload(result.archivo);
                }

                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }
            } else {
                setError(result?.mensaje || "No se pudo subir el archivo.");
            }
        } catch (err) {
            console.error("Error al subir el archivo:", err);
            setError("No se pudo subir el archivo.");
        }

        setIsLoading(false);
    };

    const handleDeleteFile = async (fileId) => {
        const confirmDelete = window.confirm("¿Estás seguro de que deseas eliminar este archivo?");
        if (!confirmDelete) return;

        try {
            const result = await eliminarArchivo({archivoId: fileId});

            if (result.success) {
                setFiles(files.filter((file) => file.id !== fileId));
                setError("");
            } else {
                setError(result.mensaje);
            }
        } catch (err) {
            console.error("Error al eliminar el archivo:", err);
            setError("No se pudo eliminar el archivo.");
        }
    };

    // Nueva función: handleArchivarFile
    const handleArchivarFile = async (fileId) => {
        try {
            const result = await archivarArchivo({archivoId: fileId});
            if (result.success) {
                console.log("Archivo archivado con éxito.");
                // Después de archivar, volvemos a cargar los archivos para actualizar la UI.
                obtenerArchivos({seccionId, archivoId: null, archivado})
                    .then((result) => {
                        if (result && result.success) {
                            setFiles(result.archivos);
                            setError("");
                        } else {
                            setFiles([]);
                            setError("No se pudieron cargar los archivos / No hay archivos.");
                        }
                    })
                    .catch((err) => {
                        console.error("Error al recargar los archivos:", err);
                        setFiles([]);
                        setError("No se pudieron cargar los archivos / No hay archivos.");
                    });
            } else {
                setError(result.mensaje);
            }
        } catch (error) {
            console.error("Error al archivar el archivo:", error);
            setError("Error al archivar el archivo.");
        }
    };


    const getPreview = (file) => {
        if (file.tipo_mime.includes("image")) {
            return <img src={file.ruta_archivo} alt={file.nombre_archivo} className="file-preview"/>;
        } else {
            return (
                <div className="file-icon">
                    {file.tipo_mime.includes("video") ? "🎥" :
                        file.tipo_mime.includes("audio") ? "🎵" :
                            file.tipo_mime.includes("spreadsheet") ? "📊" :
                                file.tipo_mime.includes("word") ? "📑" :
                                    file.tipo_mime.includes("pdf") ? "📄" :
                                        file.tipo_mime.includes("pptx") ? "📇" :
                                            file.tipo_mime.includes("zip") ? "📦" :
                                                file.tipo_mime.includes("text") ? "📝" :
                                                    "📄"}
                </div>
            );
        }
    };

    const formatSize = (size) => {
        if (size < 1024) return `${size} bytes`;
        if (size < 1048576) return `${(size / 1024).toFixed(2)} KB`;
        return `${(size / 1048576).toFixed(2)} MB`;
    };

    return (
        <div className="gestor-archivos">
            {mostrarFormulario && (
                <div className="gestor-upload-form">
                    <input
                        type="file"
                        accept={acceptedFileTypes || "*/*"}
                        onChange={(e) => setNewFile(e.target.files[0])}
                        ref={fileInputRef}
                        className="gestor-file-input"
                    />
                    <button
                        type="button"
                        onClick={handleAddFile}
                        className="gestor-upload-button"
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <>
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                {" "}Loading...
                            </>
                        ) : (
                            "Subir Archivo"
                        )}
                    </button>
                    <label className="gestor-archivados-label">
                        <input
                            type="checkbox"
                            id="archivados-checkbox"
                            className="gestor-archivados-checkbox me-2"
                            onChange={(e) => setArchivado(e.target.checked)}
                        />
                        Archivados
                    </label>
                </div>
            )}

            {error && <p className="gestor-error-message">{error}</p>}
            {successMsg && <p style={{color: "green", fontWeight: "bold", marginTop: "8px"}}>{successMsg}</p>}

            {/* Condicional: solo muestra la grid de archivos si mostrarPreview es true */}
            {mostrarPreview && (
                <div className="gestor-file-grid">
                    {files.map((file) => (
                        <div key={file.id} className="gestor-file-card">
                            {getPreview(file)}
                            <h3 className="file-title text-break">{file.nombre_archivo}</h3>
                            <p className="file-date">Fecha: {new Date(file.fecha_subida).toLocaleDateString()}</p>
                            <p className="file-size">Tamaño: {formatSize(file.tamanio)}</p>
                            <div className="d-flex flex-column">
                                <a
                                    href={file.ruta_archivo}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="gestor-download-button"
                                >
                                    🔗 Ver Archivo
                                </a>
                                <button onClick={() => handleDeleteFile(file.id)} className="gestor-delete-button">
                                    🗑 Eliminar
                                </button>
                                <button onClick={() => handleArchivarFile(file.id)} className="gestor-archivar-button">
                                    📁 Archivar
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default GestorArchivos;